import { processSteps } from "@/mocks/services";

interface ProcessSectionProps {
  subtitle?: string;
  light?: boolean;
}

const stepImages = [
  "https://public.readdy.ai/ai/img_res/6cf1f1871d0f24ee73cf387d2118b4f6.jpg",
  "https://readdy.ai/api/search-image?query=friendly%20hvac%20technician%20explaining%20recommendations%20to%20homeowner%20on%20tablet%20showing%20clear%20options%20transparent%20communication%20living%20room%20bright%20modern%20interior%20dallas%20texas%20photorealistic&width=600&height=380&seq=proc-img-2&orientation=landscape",
  "https://readdy.ai/api/search-image?query=certified%20hvac%20technician%20precision%20repair%20work%20on%20air%20conditioning%20unit%20professional%20tools%20focused%20skilled%20hands%20close%20up%20quality%20components%20residential%20dallas%20texas%20photorealistic&width=600&height=380&seq=proc-img-3&orientation=landscape",
  "https://readdy.ai/api/search-image?query=hvac%20technician%20testing%20air%20conditioning%20system%20performance%20digital%20gauges%20pressure%20readings%20quality%20check%20final%20inspection%20residential%20home%20dallas%20texas%20professional%20photorealistic&width=600&height=380&seq=proc-img-4&orientation=landscape",
  "https://readdy.ai/api/search-image?query=happy%20homeowner%20shaking%20hands%20with%20hvac%20technician%20front%20door%20satisfied%20customer%20service%20complete%20sunny%20day%20dallas%20texas%20residential%20neighborhood%20professional%20photorealistic&width=600&height=380&seq=proc-img-5&orientation=landscape",
];

export default function ProcessSection({ subtitle, light }: ProcessSectionProps) {
  const bg = light ? "bg-white" : "bg-[#050F1A]";
  const labelColor = light ? "text-[#0EA5E9]" : "text-[#7DD3FC]";
  const headingColor = light ? "text-gray-900" : "text-white";
  const subColor = light ? "text-gray-600" : "text-white/90";

  return (
    <section className={`${bg} py-28 overflow-hidden`}>
      <div className="max-w-7xl mx-auto px-6">

        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-end mb-16">
          <div>
            <p className={`font-['Inter'] ${labelColor} text-xs font-semibold uppercase tracking-[0.18em] mb-4`}>
              How It Works
            </p>
            <h2 className={`font-['Playfair_Display'] ${headingColor} text-4xl md:text-5xl font-bold leading-tight`}>
              Our Proven Service Process
            </h2>
          </div>
          <div>
            <p className={`font-['Inter'] ${subColor} text-base leading-[1.8]`}>
              {subtitle || "A systematic, transparent approach to every service call — from first contact to long-term system reliability."}
            </p>
          </div>
        </div>

        {/* Steps Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {processSteps.map((step, idx) => (
            <div
              key={step.id}
              className="group relative rounded-2xl overflow-hidden border border-white/10 hover:border-[#0EA5E9]/50 transition-all duration-300 min-h-[300px] flex flex-col"
            >
              {/* Background image */}
              <div className="absolute inset-0 w-full h-full">
                <img
                  src={stepImages[idx]}
                  alt={step.title}
                  className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                />
                {/* Dark overlay — heavy at bottom for text */}
                <div className="absolute inset-0 bg-gradient-to-t from-[#020b14]/98 via-[#020b14]/70 to-[#020b14]/30" />
              </div>

              {/* Connector arrow — desktop only */}
              {idx < processSteps.length - 1 && (
                <div className="hidden lg:flex absolute top-4 -right-2.5 z-20 items-center justify-center w-5 h-5 rounded-full bg-[#0EA5E9]/20 border border-[#0EA5E9]/40">
                  <i className="ri-arrow-right-s-line text-[#7DD3FC] text-xs"></i>
                </div>
              )}

              {/* Content */}
              <div className="relative z-10 flex flex-col flex-1 p-6">
                {/* Step number badge */}
                <div className="mb-auto">
                  <div className="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-br from-[#0EA5E9] to-[#38bdf8] shadow-[0_4px_16px_rgba(14,165,233,0.35)]">
                    <span className="font-['Playfair_Display'] text-white font-bold text-lg leading-none">
                      {step.number}
                    </span>
                  </div>
                </div>

                {/* Text block at bottom */}
                <div className="mt-16 pt-4 border-t border-white/20">
                  <h3 className="font-['Inter'] text-white font-bold text-[0.9375rem] mb-2 leading-snug drop-shadow-[0_1px_4px_rgba(0,0,0,0.9)]">
                    {step.title}
                  </h3>
                  <p className="font-['Inter'] text-white text-xs leading-[1.8] opacity-90 drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">
                    {step.description}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 flex items-center justify-center">
          <a
            href="#quote"
            className={`inline-flex items-center gap-2 font-['Inter'] text-sm font-semibold transition-all duration-300 whitespace-nowrap cursor-pointer ${light ? "text-[#0EA5E9] hover:text-[#0284C7]" : "text-[#7DD3FC] hover:text-white"}`}
          >
            Ready to get started?
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </a>
        </div>
      </div>
    </section>
  );
}
